// `search.page.tpl.html:451` — the criteria builder's third section.
//
// ⚠️ IT IS NOT A REPEATING GRID LIKE THE FIRST TWO. Work experience is five FIXED sub-sections,
// each a single criterion with its own "when": company industries, company name, company types,
// company tiers, company quality, and candidate turnover. There is no Add button and nothing to
// remove, so the `criteria-table` / `criteria-row` structure the other sections use is absent
// here and the markup is `sub-section` divs of `form-group`s instead.
//
// The repeated shape is a CHECKBOX AND A PERIOD SELECT side by side, four times over
// (`company-types`, `company-tiers`, `company-quality`, `candidate-turnover`), always inside a
// `company-types-container` — including in the three blocks that are not about company types.
// That class name is upstream's and is what the layout keys off, so it stays.
import type { ReactElement } from 'react'
import { WooCheckbox } from '../common/WooCheckbox.tsx'
import { TagsInput, type Tag } from '../common/TagsInput.tsx'
import { tagsPlaceholder } from './SkillSetSection.tsx'
import {
  LAST_USED_OPTIONS,
  TURNOVER_CONDITION_OPTIONS,
  TURNOVER_RATE_OPTIONS,
  type CriteriaConditionType,
  type LastUsedPeriod,
} from './criteriaOptions.ts'

/** Each block is a value plus the period it applies over. */
export interface PeriodCriterion {
  important?: boolean
  period?: LastUsedPeriod
}

export interface WorkExperienceCriteria {
  industries?: { industries?: Tag[]; period?: LastUsedPeriod }
  companies?: { companies?: Tag[]; period?: LastUsedPeriod }
  companyTypes?: { startup?: boolean; corporate?: boolean; period?: LastUsedPeriod }
  companyTiers?: PeriodCriterion
  qualityCompanyOnly?: PeriodCriterion
  candidateTurnover?: { important?: boolean; condition?: CriteriaConditionType; minTurnoverRate?: number }
}

export interface WorkExperienceSectionProps {
  /** `typeaheads.getExtensionIndustries($query, true)`. */
  loadIndustries?: (query: string) => Promise<readonly Tag[]>
  criteria: WorkExperienceCriteria
  /** `ctrl.isCompanyNameSearchEnabled` — the company-name block is behind it entirely. */
  companyNameSearchEnabled?: boolean
  industrySuggestions?: readonly Tag[]
  onIndustryQueryChange?: (query: string) => void
  onChange?: (criteria: WorkExperienceCriteria) => void
}

/**
 * The period select. Six of them, in TWO different shapes.
 *
 * ⚠️ THE TWO TAG-FIELD BLOCKS ARE NOT THE FOUR CHECKBOX BLOCKS. Industries and company name
 * render a `When` LABEL and wrap the select in its own `.select` div
 * (`<div class="form-group last-period-dropdown mL10"><label>When</label><div class="select">`);
 * the four checkbox blocks put `select` ON the form-group and have no label at all
 * (`<div class="form-group select mL25 last-period-dropdown">`). One component for all six —
 * which is what the identical `<select>` inside them invites — drops two labels and moves the
 * class that positions the control.
 */
function PeriodSelect({
  value,
  onChange,
  className = 'form-group select mL25 last-period-dropdown',
  label,
}: {
  value?: LastUsedPeriod
  onChange: (period: LastUsedPeriod) => void
  className?: string
  /** Present only on the two blocks that have one; it also brings the inner wrapper. */
  label?: string
}): ReactElement {
  const select = (
    <select
      className="form-control input"
      value={value ?? ''}
      onChange={(event) => onChange(Number(event.target.value) as LastUsedPeriod)}
    >
      {LAST_USED_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {option.display}
        </option>
      ))}
    </select>
  )

  if (label === undefined) return <div className={className}>{select}</div>
  return (
    <div className={className}>
      <label className="bold-soft">{label}</label>
      <div className="select">{select}</div>
    </div>
  )
}

export function WorkExperienceSection({
  criteria,
  companyNameSearchEnabled = false,
  industrySuggestions = [],
  onIndustryQueryChange,
  onChange,
  loadIndustries,
}: WorkExperienceSectionProps): ReactElement {
  const patch = (next: Partial<WorkExperienceCriteria>): void => onChange?.({ ...criteria, ...next })

  return (
    <>
      <div className="industries-criteria sub-section">
        <div className="industries-container">
          <div className="form-group industries-size">
            <label className="bold-soft">Company industries</label>
            <TagsInput
              name="industries"
              className="or-in-between"
              placeholder={tagsPlaceholder(criteria.industries?.industries, 'industry')}
              tags={criteria.industries?.industries ?? []}
              indicator
              indicatorClassName="fas fa-search"
              suggestions={industrySuggestions}
              onQueryChange={onIndustryQueryChange}
              {...(loadIndustries === undefined ? {} : { loadSuggestions: loadIndustries })}
              onAdd={(tag) =>
                patch({
                  industries: {
                    ...criteria.industries,
                    industries: [...(criteria.industries?.industries ?? []), tag],
                  },
                })
              }
              onRemove={(at) =>
                patch({
                  industries: {
                    ...criteria.industries,
                    industries: (criteria.industries?.industries ?? []).filter((_, i) => i !== at),
                  },
                })
              }
            />
          </div>
          {/* `mL10` here, not `mL25` — the industries block indents its period differently
              from the four checkbox blocks below. */}
          <PeriodSelect
            className="form-group last-period-dropdown mL10"
            label="When"
            value={criteria.industries?.period}
            onChange={(period) => patch({ industries: { ...criteria.industries, period } })}
          />
        </div>
      </div>

      {companyNameSearchEnabled ? (
        <div className="company-name-criteria sub-section">
          <div className="company-name-container">
            <div className="form-group company-name-size">
              <label className="bold-soft">Company name</label>
              {/* ⚠️ NO `add-from-autocomplete-only` ON THIS ONE. Every other tags-input in the
                  builder requires a suggestion; a company name is free text, because a user
                  may search for a company Woo has never indexed. */}
              <TagsInput
                name="companyName"
                className="or-in-between"
                placeholder={tagsPlaceholder(criteria.companies?.companies, 'company name')}
                tags={criteria.companies?.companies ?? []}
                freeText
                onAdd={(tag) =>
                  patch({
                    companies: { ...criteria.companies, companies: [...(criteria.companies?.companies ?? []), tag] },
                  })
                }
                onRemove={(at) =>
                  patch({
                    companies: {
                      ...criteria.companies,
                      companies: (criteria.companies?.companies ?? []).filter((_, i) => i !== at),
                    },
                  })
                }
              />
            </div>
            <PeriodSelect
              className="form-group last-period-dropdown mL10"
              label="When"
              value={criteria.companies?.period}
              onChange={(period) => patch({ companies: { ...criteria.companies, period } })}
            />
          </div>
        </div>
      ) : null}

      <div className="company-criteria sub-section">
        <div>
          <label className="bold-soft">Experience in company types</label>
        </div>

        <div className="company-types">
          <div className="company-types-container">
            <div className="form-group">
              <WooCheckbox
                checked={criteria.companyTypes?.startup === true}
                onChange={(startup) => patch({ companyTypes: { ...criteria.companyTypes, startup } })}
              >
                Startup
              </WooCheckbox>
              <WooCheckbox
                className="mL15"
                checked={criteria.companyTypes?.corporate === true}
                onChange={(corporate) => patch({ companyTypes: { ...criteria.companyTypes, corporate } })}
              >
                Corporate
              </WooCheckbox>
            </div>
            <PeriodSelect
              value={criteria.companyTypes?.period}
              onChange={(period) => patch({ companyTypes: { ...criteria.companyTypes, period } })}
            />
          </div>
        </div>

        <div className="company-tiers">
          <div className="company-types-container">
            <div className="form-group">
              <WooCheckbox
                checked={criteria.companyTiers?.important === true}
                onChange={(important) => patch({ companyTiers: { ...criteria.companyTiers, important } })}
              >
                Worked at top tier companies
              </WooCheckbox>
            </div>
            <PeriodSelect
              value={criteria.companyTiers?.period}
              onChange={(period) => patch({ companyTiers: { ...criteria.companyTiers, period } })}
            />
          </div>
        </div>

        <div className="company-quality">
          <div className="company-types-container">
            <div className="form-group">
              {/* ⚠️ THE FIELD IS `qualityCompanyOnly.important` AND THE LABEL IS AN EXCLUSION.
                  Ticking "Exclude candidates that worked at low-end companies" sets a flag
                  named for the opposite; the name is the criterion, the label is the effect. */}
              <WooCheckbox
                checked={criteria.qualityCompanyOnly?.important === true}
                onChange={(important) => patch({ qualityCompanyOnly: { ...criteria.qualityCompanyOnly, important } })}
              >
                Exclude candidates that worked at low-end companies
              </WooCheckbox>
            </div>
            <PeriodSelect
              value={criteria.qualityCompanyOnly?.period}
              onChange={(period) => patch({ qualityCompanyOnly: { ...criteria.qualityCompanyOnly, period } })}
            />
          </div>
        </div>

        <div className="candidate-turnover">
          <div className="company-types-container">
            <div className="form-group">
              <WooCheckbox
                checked={criteria.candidateTurnover?.important === true}
                onChange={(important) => patch({ candidateTurnover: { ...criteria.candidateTurnover, important } })}
              >
                Candidates with high turnover rate
              </WooCheckbox>
            </div>
            {/* ⚠️ THIS BLOCK HAS NO PERIOD. Its two selects are a CONDITION and a RATE — the
                only one of the four whose second control is not `lastUsedDisplayText`, and the
                classes differ too (`mL25` then `mL10`, no `last-period-dropdown`). */}
            <div className="form-group select mL25">
              <select
                className="form-control input"
                value={criteria.candidateTurnover?.condition ?? ''}
                onChange={(event) =>
                  patch({
                    candidateTurnover: {
                      ...criteria.candidateTurnover,
                      condition: Number(event.target.value) as CriteriaConditionType,
                    },
                  })
                }
              >
                {TURNOVER_CONDITION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.display}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group select mL10">
              <select
                className="form-control input"
                value={criteria.candidateTurnover?.minTurnoverRate ?? ''}
                onChange={(event) =>
                  patch({
                    candidateTurnover: {
                      ...criteria.candidateTurnover,
                      minTurnoverRate: Number(event.target.value),
                    },
                  })
                }
              >
                {TURNOVER_RATE_OPTIONS.map((option) => (
                  <option key={option.years} value={option.years}>
                    {option.display}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
